import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../utils/Api";
import { useParams } from "react-router-dom";
import { MaterialCommunityIcons } from "@expo/vector-icons";

type Addon = {
  id: string;
  name: string;
  description?: string;
};

type Props = {
  productData: { category_id?: string };
  selectedAddons: string[];
  onChange: (selected: string[]) => void;
  onClose: () => void;
};

const AddonSelectorComponent: React.FC<Props> = ({
  productData,
  selectedAddons,
  onChange,
  onClose,
}) => {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const { brand_id } = useParams();

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [addonGroupFormData, setAddonGroupFormData] = useState({
    group_name: "",
    group_notes: "",
    is_compulsory: false,
    is_multiple: false,
    min_count: "",
    max_count: "",
    selected_addon_items: [],
  });
  const [addonGroupFormErrors, setAddonGroupFormErrors] = useState({
    group_name: "",
    group_notes: "",
    min_count: "",
    max_count: "",
    addon_items: "",
  });
  const [isAddonGroupFormValid, setIsAddonGroupFormValid] = useState(false);

  const [addonItemSearch, setAddonItemSearch] = useState("");
  const [debouncedAddonItemSearch, setDebouncedAddonItemSearch] = useState("");
  const [addonItemList, setAddonItemList] = useState<Addon[]>([]);
  const [addonItemPage, setAddonItemPage] = useState(1);
  const [addonItemNextPage, setAddonItemNextPage] = useState(true);
  const [addonItemLoading, setAddonItemLoading] = useState(false);
  const [addonItemInitialLoading, setAddonItemInitialLoading] = useState(true);
  const addonItemLoaderRef = useRef(null);
  const isFetchingAddonItems = useRef(false);

  const [selectedAddonItemIds, setSelectedAddonItemIds] = useState<string[]>(
    []
  );
  const [showCreateAddonItem, setShowCreateAddonItem] = useState(false);
  const [addonItemFormData, setAddonItemFormData] = useState({
    item_name: "",
    item_notes: "",
    custom_quantity: false,
    diet_type: {},
  });
  const [addonItemFormErrors, setAddonItemFormErrors] = useState({
    item_name: "",
    item_notes: "",
    diet_type: "",
  });
  const [isAddonItemFormValid, setIsAddonItemFormValid] = useState(false);


  const [savingGroup, setSavingGroup] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  // Debounce Addon Group Search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setAddons([]);
      setNextPage(true);
      setInitialLoading(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Debounce Addon Item Search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showCreateGroup) {
        setDebouncedAddonItemSearch(addonItemSearch);
        setAddonItemPage(1);
        setAddonItemList([]);
        setAddonItemNextPage(true);
        setAddonItemInitialLoading(true);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [addonItemSearch, showCreateGroup]);

  // Fetch Addon Groups
  const fetchAddons = useCallback(async () => {
    if (loading || !nextPage || !brand_id) return;
    setLoading(true);
    try {
      const pass_data = {
        get: {
          brand: brand_id,
          page,
          count: "all",
          sort: "asc",
          search: debouncedSearch,
          spec: "",
          mrp: encodeURI(JSON.stringify([1, 2, 3, 4])),
          addon_groups: "",
          category_id: productData?.category_id,
        },
      };
      const res: any = await api("/brand/get_all_addon_groups", pass_data);
      const data = await res.response;
      setAddons((prev) => (page === 1 ? data.addon : [...prev, ...data.addon]));
      setNextPage(!!data.next_page);
    } catch (err) {
      console.error("Failed to fetch addons", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    brand_id,
    productData?.category_id,
    loading,
    nextPage,
  ]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  // Observer for Addon Group Pagination
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && nextPage) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [loading, nextPage]);

  // Fetch Addon Items
  const fetchAddonItems = useCallback(async () => {
    if (
      !showCreateGroup ||
      !brand_id ||
      isFetchingAddonItems.current ||
      !addonItemNextPage
    )
      return;

    isFetchingAddonItems.current = true;
    addonItemPage === 1
      ? setAddonItemInitialLoading(true)
      : setAddonItemLoading(true);

    try {
      const res: any = await api("/brand/get_all_addon_items", {
        get: {
          brand: brand_id,
          search: debouncedAddonItemSearch,
          page: addonItemPage,
          count: 10,
        },
      });

      const data = await res.response;
      setAddonItemList((prev) =>
        addonItemPage === 1 ? data.data || [] : [...prev, ...(data.data || [])]
      );
      setAddonItemNextPage(data.next_page);
    } catch (err) {
      console.error("Addon items fetch failed", err);
    } finally {
      addonItemPage === 1
        ? setAddonItemInitialLoading(false)
        : setAddonItemLoading(false);
      isFetchingAddonItems.current = false;
    }
  }, [
    showCreateGroup,
    brand_id,
    addonItemPage,
    debouncedAddonItemSearch,
    addonItemNextPage,
  ]);

  useEffect(() => {
    if (!showCreateGroup) return;
    fetchAddonItems();
  }, [
    fetchAddonItems,
    showCreateGroup,
    addonItemPage,
    debouncedAddonItemSearch,
  ]);

  // Observer for Addon Item Pagination
  useEffect(() => {
    if (!showCreateGroup) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          addonItemNextPage &&
          !addonItemLoading &&
          !isFetchingAddonItems.current
        ) {
          setAddonItemPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    const current = addonItemLoaderRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [showCreateGroup, addonItemNextPage, addonItemLoading]);

  const fetchSpecifications = useCallback(async () => {
    try {
      const res: any = await api("/brand/init_specifications", {
        get: {
          brand: brand_id,
          specifications_only: "1",
        },
      });
      const data = await res.response;
    } catch (err) {
      console.error("specification fetch failed", err);
    } finally {
    }
  }, [showCreateAddonItem]);

  const toggleAddonItemSelection = (item: Addon) => {
    setSelectedAddonItemIds((prev: any) => {
      const exists = prev.find((i: any) => i.id === item.id);
      if (exists) {
        return prev.filter((i: any) => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSaveGroup = () => {
    const payload = {
      form_data: addonGroupFormData,
      addon_items: selectedAddonItemIds,
    };
    console.log("Save Group Payload:", payload);
    let sendData: any = {
      name: addonGroupFormData.group_name,
      notes: addonGroupFormData.group_notes,
      category_id: productData?.category_id,
      addons: [],
      compulsory: addonGroupFormData.is_compulsory,
    };
    selectedAddonItemIds.map((ele: any) => {
      sendData.addons.push({ addon_id: ele.id });
    });
    if (addonGroupFormData.is_multiple) {
      sendData.min = addonGroupFormData.min_count;
      sendData.max = addonGroupFormData.max_count;
    }
    createAddonGroup(sendData);
    // setSelectedAddonItemIds([]);
    // setShowCreateGroup(false);
  };

  const validateAddonGroupForm = () => {
    const errors: any = {};
    const { group_name, group_notes, is_multiple, min_count, max_count } =
      addonGroupFormData;

    if (!group_name.trim()) {
      errors.group_name = "Group name is required.";
    }

    if (!group_notes.trim()) {
      errors.group_notes = "Group notes are required.";
    }

    if (is_multiple) {
      const min = parseInt(min_count);
      const max = parseInt(max_count);

      if (isNaN(min)) {
        errors.min_count = "Min count is required.";
      }

      if (isNaN(max)) {
        errors.max_count = "Max count is required.";
      }

      if (!isNaN(min) && !isNaN(max)) {
        if (min < 0) {
          errors.min_count = "Min count must be >= 0.";
        }
        if (max <= min) {
          errors.max_count = "Max count must be greater than min count.";
        }
      }
    }

    if (selectedAddonItemIds.length === 0) {
      errors.addon_items = "At least one addon item must be selected.";
    }

    setAddonGroupFormErrors(errors);
    setIsAddonGroupFormValid(Object.keys(errors).length === 0);
  };

  const createAddonGroup = async (getData: any) => {
    let passData: any = {
      get: {
        brand: brand_id,
      },
      post: {
        name: getData.name,
        notes: getData.notes,
        category_id: getData.category_id,
        addons: getData.addons,
        compulsory: getData.compulsory,
      },
    };
    if (getData.hasOwnProperty("min")) {
      passData.post.min = getData.min;
      passData.post.max = getData.max;
    }
    setSavingGroup(true);
    try {
      const res: any = await api("/brand/create_addon_group", passData);
      const data = await res.response;
    } catch (err) {
      console.error("create addon group failed", err);
    } finally {
      setSavingGroup(false);
    }
  };

  useEffect(() => {
    if (showCreateGroup) {
      validateAddonGroupForm();
    }
  }, [addonGroupFormData, selectedAddonItemIds]);

  const handleSaveItem = () => {
    const payload = {
      form_data: addonItemFormData,
    };
    console.log("Save item Payload:", payload);
    // let sendData: any = {
    //   name: addonGroupFormData.group_name,
    //   notes: addonGroupFormData.group_notes,
    //   category_id: productData?.category_id,
    //   addons: [],
    //   compulsory: addonGroupFormData.is_compulsory,
    // };
    // selectedAddonItemIds.map((ele: any) => {
    //   sendData.addons.push({ addon_id: ele.id });
    // });
    // if (addonGroupFormData.is_multiple) {
    //   sendData.min = addonGroupFormData.min_count;
    //   sendData.max = addonGroupFormData.max_count;
    // }
    // createAddonGroup(sendData);
  };

  const validateAddonItemForm = () => {
    const errors: any = {};
    const { item_name, item_notes} =
      addonItemFormData;

    if (!item_name.trim()) {
      errors.item_name = "Item name is required.";
    }

    if (!item_notes.trim()) {
      errors.item_notes = "Item notes are required.";
    }

    setAddonItemFormErrors(errors);
    setIsAddonItemFormValid(Object.keys(errors).length === 0);
  };

  useEffect(() => {
    if (showCreateAddonItem) {
      validateAddonItemForm();
    }
  }, [addonItemFormData]);

  const renderSkeletons = (count: number) =>
    Array.from({ length: count }).map((_, index) => (
      <div className="col-12 col-md-6 col-lg-4 mb-3" key={`skeleton-${index}`}>
        <div className="card p-3">
          <div className="placeholder-glow">
            <span className="placeholder col-6 mb-2"></span>
            <span className="placeholder col-8"></span>
          </div>
        </div>
      </div>
    ));

  return (
    <div className="">
      {/* header */}
      <div className=" px-2 py-3 d-flex bg-fff center sticky-sm shadow">
        {showCreateAddonItem ? (
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setShowCreateAddonItem(!showCreateAddonItem);
              }}
            >
              <MaterialCommunityIcons
                name="arrow-left"
                size={20}
                color="black"
              />
            </div>
            <p className="px-2 fs-14px text-black">Create Addon Item</p>
          </div>
        ) : showCreateGroup ? (
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setShowCreateGroup(!showCreateGroup);
              }}
            >
              <MaterialCommunityIcons
                name="arrow-left"
                size={20}
                color="black"
              />
            </div>
            <p className="px-2 fs-14px text-black">Create Addon Group</p>
          </div>
        ) : (
          <p className="fs-14px text-black">Add Addon Group</p>
        )}
        <div className="ms-auto">
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              onClose();
            }}
          ></button>
        </div>
      </div>

      {/* Addon Item Creation */}
      {/* Create Addon Group Section */}
      {/* Addon Groups List */}

      {showCreateAddonItem ? (
        <div className="p-3 mt-3">
          {/* item name */}
          <div className="mb-3">
            <label className="form-label">Addon Item Name</label>
            <input
              className="form-control"
              value={addonItemFormData.item_name}
              onChange={(e) => {
                setAddonItemFormData((prevalue: any) => {
                  prevalue.item_name = e.target.value;
                  return { ...prevalue };
                });
              }}
            />
          </div>
          {/* item notes */}
          <div className="mb-3">
            <label className="form-label">Addon Items Notes</label>
            <textarea
              className="form-control"
              value={addonItemFormData.item_notes}
              onChange={(e) => {
                setAddonItemFormData((prevalue: any) => {
                  prevalue.item_notes = e.target.value;
                  return { ...prevalue };
                });
              }}
            />
          </div>
          {/* custom quantity */}
          <div className="d-flex align-items-center mb-3">
            <label className="form-label">Custom Quantity</label>
            <div className="ms-auto align-items-center">
              <label className="in-stock-switch cursor">
                <input
                  type="checkbox"
                  checked={addonItemFormData.custom_quantity}
                  onChange={(e) => {
                    setAddonItemFormData((prevalue: any) => {
                      prevalue.custom_quantity = e.target.checked;
                      return { ...prevalue };
                    });
                  }}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>

          <div className="mt-5 mb-2 fix-bottom">
            <button
              className={`${
                isAddonItemFormValid ? "primary-button" : "disabled-button"
              }`}
              onClick={handleSaveItem}
              disabled={!isAddonItemFormValid}
            >
              {savingItem ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              ) : null}
              {savingItem ? "Creating..." : "Create Addon Item"}
              {/* Create Addon Group */}
            </button>
          </div>
        </div>
      ) : showCreateGroup ? (
        <div className="">
          <div className="p-3 mt-2 mb-4">
            {/* Addon Group Name */}
            <div className="mb-3">
              <label className="form-label">Addon Group Name</label>
              <input
                type="text"
                className="form-control"
                value={addonGroupFormData.group_name}
                onChange={(e) => {
                  setAddonGroupFormData((prevalue: any) => {
                    prevalue.group_name = e.target.value;
                    return { ...prevalue };
                  });
                }}
              />
            </div>
            {/* Addon Group Notes */}
            <div className="mb-3">
              <label className="form-label">Addon Group Notes</label>
              <textarea
                className="form-control"
                value={addonGroupFormData.group_notes}
                onChange={(e) => {
                  setAddonGroupFormData((prevalue: any) => {
                    prevalue.group_notes = e.target.value;
                    return { ...prevalue };
                  });
                }}
              />
            </div>
            {/* is compulsory */}
            <div className="d-flex align-items-center mb-3">
              <label className="form-label">Is Compulsory</label>
              <div className="ms-auto align-items-center">
                <label className="in-stock-switch cursor">
                  <input
                    type="checkbox"
                    checked={addonGroupFormData.is_compulsory}
                    onChange={(e) => {
                      setAddonGroupFormData((prevalue: any) => {
                        prevalue.is_compulsory = e.target.checked;
                        return { ...prevalue };
                      });
                    }}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
            {/* is multiple */}
            <div className="d-flex align-items-center mb-3">
              <label className="form-label">Is Multiple</label>
              <div className="ms-auto align-items-center">
                <label className="in-stock-switch cursor">
                  <input
                    type="checkbox"
                    checked={addonGroupFormData.is_multiple}
                    onChange={(e) => {
                      setAddonGroupFormData((prevalue: any) => {
                        prevalue.is_multiple = e.target.checked;
                        return { ...prevalue };
                      });
                    }}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
            {/* min / max count */}
            {addonGroupFormData.is_multiple && (
              <>
                <div className="d-flex">
                  <div className="pe-2">
                    <label className="form-label">Min Count</label>
                    <input
                      type="number"
                      className="form-control"
                      value={addonGroupFormData.min_count}
                      onChange={(e) => {
                        setAddonGroupFormData((prevalue: any) => {
                          prevalue.min_count = e.target.value;
                          return { ...prevalue };
                        });
                      }}
                    />
                  </div>
                  <div className="">
                    <label className="form-label">Max Count</label>
                    <input
                      type="number"
                      className="form-control"
                      value={addonGroupFormData.max_count}
                      onChange={(e) => {
                        setAddonGroupFormData((prevalue: any) => {
                          prevalue.max_count = e.target.value;
                          return { ...prevalue };
                        });
                      }}
                    />
                  </div>
                </div>
                {addonGroupFormErrors.max_count && (
                  <div className="fs-11px text-danger my-2">
                    {addonGroupFormErrors.max_count}
                  </div>
                )}
              </>
            )}
            {/* Addon Item Selector */}
            <div className="mb-3 border-top mt-2">
              <label htmlFor="" className="py-3">
                Addon Items :
              </label>
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="Search Addon items..."
                  value={addonItemSearch}
                  onChange={(e) => setAddonItemSearch(e.target.value)}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCreateAddonItem(true);
                    fetchSpecifications();
                  }}
                >
                  Create
                </button>
              </div>

              {addonGroupFormErrors.addon_items && (
                <div className="fs-11px text-danger mt-2">
                  {addonGroupFormErrors.addon_items}
                </div>
              )}

              <div className="row pt-3 mb-5 pb-5">
                {addonItemList.map((item) => (
                  <div className="form-check col-6 col-md-6 mb-2" key={item.id}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`item-${item.id}`}
                      checked={selectedAddonItemIds.some(
                        (i: any) => i.id === item.id
                      )}
                      onChange={() => toggleAddonItemSelection(item)}
                    />
                    <label
                      className="fs-13px form-check-label"
                      htmlFor={`item-${item.id}`}
                    >
                      {item.name}
                    </label>
                  </div>
                ))}

                {addonItemInitialLoading && (
                  <div className="text-muted text-center py-4">
                    Loading addon items...
                  </div>
                )}

                {!addonItemInitialLoading && addonItemList.length === 0 && (
                  <div className="text-muted text-center py-4">
                    No addon items found
                  </div>
                )}

                {addonItemLoading && (
                  <div className="text-center w-100 py-3 text-muted">
                    Loading more...
                  </div>
                )}

                <div ref={addonItemLoaderRef} className="w-100" />
              </div>
            </div>
          </div>
          <div className="mt-5 mb-2 fix-bottom">
            <button
              className={`${
                isAddonGroupFormValid ? "primary-button" : "disabled-button"
              }`}
              onClick={handleSaveGroup}
              disabled={!isAddonGroupFormValid}
            >
              {savingGroup ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              ) : null}
              {savingGroup ? "Creating..." : "Create Addon Group"}
              {/* Create Addon Group */}
            </button>
          </div>
        </div>
      ) : (
        <div className="">
          <div className="bg-fff mt-2 p-2 overflow-auto">
            {/* Search + Add Group Button */}
            <div className="d-flex mb-3 align-items-center">
              <input
                type="text"
                className="form-control me-2"
                placeholder="Search addon groups..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {/* {search && (
                <button className="me-2" onClick={handleClearSearch}>
                  X
                </button>
              )} */}
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateGroup(!showCreateGroup)}
              >
                Create
              </button>
            </div>
            <div className="row pb-5">
              {addons.length === 0 && !initialLoading && !loading && (
                <div className="col-12 text-center text-muted py-4">
                  No addon groups found
                </div>
              )}
              {addons.map((addon, index) => (
                <div
                  className={`col-12 col-md-6 col-lg-4 ${
                    index === addons.length - 1 ? "mb-5 pb-3" : " mb-3"
                  }`}
                  // "col-12 col-md-6 col-lg-4 mb-3"
                  key={addon.id}
                >
                  <div className="card h-100 p-3">
                    <div className="d-flex">
                      <input
                        type="checkbox"
                        className="form-check-input me-2 mt-1"
                        checked={selectedAddons.includes(addon.id)}
                        onChange={() => handleToggle(addon.id)}
                      />
                      <div>
                        <h6 className="mb-1">{addon.name}</h6>
                        <p className="text-muted small mb-0">
                          {addon.description || "No description"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {initialLoading && renderSkeletons(6)}
              {loading && !initialLoading && renderSkeletons(3)}
              {/* <div ref={loaderRef} className="w-100 text-center my-3" /> */}
            </div>
            <div className="mt-5 mb-2 fix-bottom">
              <button className={"primary-button"} onClick={() => {}}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonSelectorComponent;
